/**
 * Oportunidades.
 *
 * «Descolgadas» son las que tienen fecha de cierre pasada y siguen
 * abiertas: no se han perdido, se han quedado atrás, que es distinto y se
 * arregla con una llamada.
 */

import { useSearchParams } from 'react-router-dom';
import { DataTable, type Column } from '../table/data-table.js';
import { useState } from 'react';
import { useListing } from '../table/use-listing.js';
import { CrmActions, type OpportunityRef } from './crm-actions.js';
import { money, day } from '../table/format.js';

interface Row {
  id: string;
  code: string;
  title: string;
  stage: string;
  estimated_value: string | null;
  probability: number | null;
  expected_close_date: string | null;
  closed_on: string | null;
  loss_reason: string | null;
  account_name: string | null;
  owner_name: string | null;
  days_to_close: number | null;
}


const COLUMNS: Column<Row>[] = [
  { id: 'code', label: 'Código', render: (r) => r.code, sortable: true, width: '8rem' },
  { id: 'title', label: 'Oportunidad', render: (r) => r.title },
  { id: 'accountName', label: 'Cuenta', render: (r) => r.account_name ?? '—' },
  { id: 'ownerName', label: 'Responsable', render: (r) => r.owner_name ?? 'Sin asignar' },
  { id: 'stage', label: 'Etapa', render: (r) => r.stage, sortable: true, width: '10rem' },
  {
    id: 'probability',
    label: 'Prob.',
    numeric: true,
    width: '6rem',
    render: (r) => (r.probability === null ? '—' : `${r.probability} %`),
    exportValue: (r) => r.probability ?? '',
  },
  {
    id: 'expectedCloseDate',
    label: 'Cierre previsto',
    width: '13rem',
    render: (r) => {
      if (r.expected_close_date === null) return <span className="due--none">—</span>;
      if (r.closed_on === null && (r.days_to_close ?? 0) < 0) {
        return (
          <span className="due due--late">
            ⚠ Descolgada {Math.abs(r.days_to_close ?? 0)} d
          </span>
        );
      }
      return <span className="due">{day(r.expected_close_date)}</span>;
    },
    exportValue: (r) => r.expected_close_date ?? '',
  },
  {
    id: 'estimatedValue',
    label: 'Valor',
    numeric: true,
    render: (r) => money(r.estimated_value),
    exportValue: (r) => r.estimated_value ?? '',
  },
];

const COLUMN_IDS = COLUMNS.map((c) => c.id);

const QUICK = [
  { id: 'open', label: 'Abiertas', param: { openOnly: 'true' } },
  { id: 'stale', label: 'Descolgadas', param: { stale: 'true' } },
  { id: 'all', label: 'Todas', param: {} },
] as const;

export function OpportunitiesScreen(): React.ReactNode {
  const [params] = useSearchParams();
  const quick = QUICK.find((q) => q.id === params.get('v')) ?? QUICK[0];

  const [selected, setSelected] = useState<OpportunityRef | null>(null);

  const listing = useListing<Row>({
    path: '/opportunities',
    columnIds: COLUMN_IDS,
    extraParams: quick.param,
    preserve: ['v'],
  });

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Oportunidades</h1>
        <div className="chips" role="group" aria-label="Filtros rápidos">
          {QUICK.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`chip${quick.id === option.id ? ' chip--on' : ''}`}
              aria-pressed={quick.id === option.id}
              onClick={() => listing.setParam('v', option.id === 'open' ? null : option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {listing.total !== null && <span className="screen__count">{listing.total} oportunidades</span>}
      </header>

      {listing.error !== null && (
        <p className="screen__error" role="alert">
          {listing.error}
        </p>
      )}

      <DataTable
        columns={[
          ...COLUMNS,
          {
            id: 'avanzar',
            label: '',
            width: '7rem',
            render: (r: Row) => (
              <button
                className="button button--quiet"
                type="button"
                onClick={() => setSelected({ id: r.id, title: r.title, stage: r.stage })}
              >
                Avanzar
              </button>
            ),
            exportValue: () => '',
          },
        ]}
        rows={listing.rows}
        idOf={(r) => r.id}
        state={listing.state}
        onStateChange={listing.setState}
        exportName="oportunidades"
        loading={listing.loading}
        hasMore={listing.hasMore}
        onLoadMore={listing.loadMore}
        totalMatching={listing.total}
        emptyMessage="No hay oportunidades que coincidan."
      />

      <CrmActions opportunity={selected} onDone={() => listing.reload()} />
    </section>
  );
}
